import type { MangoQuery } from 'rxdb';
import { useAtomValue } from 'jotai';

import { useSyncStatus } from '@/providers/rxdb/sync-status';

import { knownEmptyAccountAtom } from '@/state';
import { searchService } from '@/services/search-service';
import type { SortString } from '@/constants/types';
import { useSearchCore, type SearchQuery } from './use-search-core';

export interface UseWithSearchParams<T> {
  searchTerm: string;
  ids?: string[];
  tagIds?: string[];
  availabilityScopes?: string[];
  sort?: MangoQuery<T>['sort'];
  highlightMatches?: boolean;
}

export const useSearch = (query: SearchQuery, enabled: boolean = true) => {
  const { initialSyncComplete } = useSyncStatus();
  // Display-only fast path: a known/assumed-empty account skips the initial-sync
  // loading gate — real data (data.length) always wins over the loading state anyway.
  // Only queries scoped to the probed document surfaces qualify (see FAST_PATH_TYPES).
  const knownEmptyAccount = useAtomValue(knownEmptyAccountAtom);
  const { data, stats, refetch, isInitialLoading } = useSearchCore(searchService, query, enabled);
  const skipSyncGate = knownEmptyAccount && coversOnlyFastPathTypes(query);

  return {
    data,
    stats,
    refetch,
    isLoading: (!initialSyncComplete && !skipSyncGate && !data.length) || isInitialLoading,
  };
};

// Search-type mirror of documentCollections (providers/rxdb/constants.ts), the set the
// empty-account probe verifies — templates are the notes collection's search variant.
// Queries over other types (tags, instructions) keep the normal initial-sync gate.
const FAST_PATH_TYPES = ['notes', 'templates', 'userSources', 'blocks'];

// Undefined types means no type filter — the query spans every indexed type,
// including unprobed ones — so it does not qualify for the fast path.
const coversOnlyFastPathTypes = (query: SearchQuery): boolean => {
  const { types } = query.where ?? {};
  if (!types) return false;
  const typeList = Array.isArray(types) ? types : [types];
  return typeList.every((type) => FAST_PATH_TYPES.includes(type));
};

export const convertSortToString = <T>(sort?: MangoQuery<T>['sort']): SortString | undefined => {
  if (!sort?.[0]) {
    return undefined;
  }
  const sortObj = sort[0];
  const property = Object.keys(sortObj)[0];
  const order = Object.values(sortObj)[0];
  return `${property}:${order}` as SortString;
};
